import { DEFER_COLLAB_SEND } from '@atlaskit/editor-common/collab-defer-collab-send';
import { STREAMING_COLLAPSIBLE_STEPS } from '@atlaskit/editor-common/collab-streaming-step-collapse';
import { Slice } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { countCompleteBlocks } from '../../../ui/utils/complete-blocks';
import {
	isQuickActionConfigItem,
	resolveAgentAttribution,
} from '../../agent-attribution/resolveAgentAttribution';
import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import {
	isImproveDescriptionSyncBlockFixEnabled,
	withoutSyncBlocks,
} from '../../improve-description-sync-block';
import { purePmInEditorDirectStreamingPluginKey } from '../pluginKey';
import { updateStreamPositions, metaTransaction } from '../transforms';
import type { PurePmInEditorDirectStreamingPluginState } from '../types';
import {
	resolveContentFragment,
	fragmentIsHealthy,
	calculateTelepointerPos,
} from '../utils/adfConversion';
import { clampPosition } from '../utils/positionValidation';
import type { FireAnalyticsStep } from './buildMountTransaction';
import { applyCellReplacementAttributes } from './cellAttributeAdjustments';
import {
	calculateEmptySelectionAdjustment,
	calculateBlockMarkAdjustment,
	calculateRangeSelectionAdjustment,
	calculateListAdjustment,
} from './positionAdjustments';
import { mapStartPos, mapToPos } from './positionMapping';
import {
	calculateTableAdjustment,
	calculateSyncBlockAdjustment,
	calculateSliceOpenStartEndAdjustment,
} from './sliceAdjustments';
import { stampUnifiedAgentEditChrome } from './stampUnifiedAgentEditChrome';

/**
 * Builds transaction for processing a new content chunk.
 */
export function buildUpdateTransaction(
	state: EditorState,
	fireAnalyticsStep: FireAnalyticsStep,
): Transaction | null {
	const decorationPluginState = aiExperienceDecorationPluginKey.getState(state);
	const { positions } = decorationPluginState ?? {};
	const sessionState = aiSessionContextPluginKey.getState(state);
	if (!sessionState) {
		return null;
	}
	const { adfResponse, markdown, idMap, responseVersion } = sessionState;
	const streamingState = purePmInEditorDirectStreamingPluginKey.getState(state);

	if (!positions || !streamingState) {
		return null;
	}

	const {
		startPos,
		userContentEndPos,
		aiContentEndPos,
		posAdjusted,
		streamTimeStamp,
		collabFlushedBlockCount,
	} = streamingState;

	if (startPos === undefined || userContentEndPos === undefined) {
		return buildPositionOnlyUpdate(state, streamingState, responseVersion);
	}

	// Resolve content using the prioritised conversion chain:
	// ADF → experiment-gated markdown → free-gen markdown fallback (with incomplete tag guard)
	const isSyncBlockFixEnabled = isImproveDescriptionSyncBlockFixEnabled(
		decorationPluginState?.configItem?.key,
	);
	const resolvedFragment = resolveContentFragment(state.schema, adfResponse, markdown, idMap, {
		skipIncompleteCustomTags: true,
	});
	const fragment =
		resolvedFragment && isSyncBlockFixEnabled
			? withoutSyncBlocks(resolvedFragment)
			: resolvedFragment;
	if (!fragment) {
		return buildPositionOnlyUpdate(state, streamingState, responseVersion);
	}

	let slice = Slice.maxOpen(fragment);
	if (!slice.content.firstChild) {
		slice = Slice.empty;
	}

	if (slice.size <= 0) {
		return buildPositionOnlyUpdate(state, streamingState, responseVersion);
	}

	if (!fragmentIsHealthy(fragment)) {
		return buildPositionOnlyUpdate(state, streamingState, responseVersion);
	}

	const docSize = state.doc.content.size;
	let workingStartPos = clampPosition(startPos, docSize);
	let workingUserContentEndPos = clampPosition(userContentEndPos, docSize);
	let workingPosAdjusted = posAdjusted;

	// Position adjustments (once per stream)
	if (!posAdjusted) {
		const isEmptySelection = positions[0] === positions[1];

		if (isEmptySelection) {
			const adjustment = calculateEmptySelectionAdjustment({
				state,
				fragment,
				startPos: workingStartPos,
				userContentEndPos: workingUserContentEndPos,
			});
			if (adjustment.adjusted) {
				workingStartPos = adjustment.startPos;
				workingUserContentEndPos = adjustment.userContentEndPos;
			}
		} else {
			const adjustment = calculateRangeSelectionAdjustment({
				state,
				fragment,
				startPos: workingStartPos,
				userContentEndPos: workingUserContentEndPos,
			});
			if (adjustment.adjusted) {
				workingStartPos = adjustment.startPos;
			}

			const listAdjustment = calculateListAdjustment({
				state,
				fragment,
				startPos: workingStartPos,
				userContentEndPos: workingUserContentEndPos,
			});
			if (listAdjustment.adjusted) {
				workingStartPos = listAdjustment.startPos;
				workingUserContentEndPos = listAdjustment.userContentEndPos;
			}
		}

		workingPosAdjusted = true;
	}

	// Block mark adjustment — runs on every update (not guarded by posAdjusted)
	// because the first streaming chunk may not yet contain the block marks.
	const blockMarkAdjustment = calculateBlockMarkAdjustment({
		state,
		fragment,
		startPos: workingStartPos,
		userContentEndPos: workingUserContentEndPos,
	});
	if (blockMarkAdjustment.adjusted) {
		workingStartPos = blockMarkAdjustment.startPos;
	}

	// Calculate toPos
	let toPos = Math.min(
		Math.max(aiContentEndPos ?? 0, workingStartPos + slice.size + 1),
		workingUserContentEndPos,
	);

	// Table adjustment
	const tableAdjustment = calculateTableAdjustment({
		state,
		fragment,
		startPos: workingStartPos,
		toPos,
		userContentEndPos: workingUserContentEndPos,
	});
	if (tableAdjustment.adjusted) {
		workingStartPos = tableAdjustment.startPos;
		toPos = tableAdjustment.toPos;
	}

	// Sync block adjustment
	const syncBlockAdjustment = calculateSyncBlockAdjustment({
		state,
		fragment,
		startPos: workingStartPos,
		toPos,
		userContentEndPos: workingUserContentEndPos,
	});
	if (syncBlockAdjustment.adjusted) {
		workingStartPos = syncBlockAdjustment.startPos;
		toPos = syncBlockAdjustment.toPos;
	}

	// Slice adjustment
	slice = calculateSliceOpenStartEndAdjustment({
		state,
		slice,
		startPos: workingStartPos,
		toPos,
	});

	// Build transaction
	let tr = state.tr;
	const safeToPos = Math.max(workingStartPos, Math.min(toPos, tr.doc.content.size));

	try {
		tr = tr.replaceRange(workingStartPos, safeToPos, slice);
	} catch {
		return buildPositionOnlyUpdate(state, streamingState, responseVersion);
	}

	try {
		tr = applyCellReplacementAttributes({
			tr,
			originalDoc: state.doc,
			replacementContent: fragment,
			adfResponse,
			startPos: workingStartPos,
			endPos: workingUserContentEndPos,
		});
	} catch {
		// Keep the replaced content when optional cell styling cannot be restored.
	}

	tr = tr.setMeta('ignoreLocalIdUpdate', true);
	// This frame replaces the whole region the previous frame wrote, so it is an intermediate
	// render of a single logical edit rather than an edit in its own right. Let the collab layer
	// compose adjacent frames into one step instead of sending every rendered chunk. This does not
	// affect the local document — see `collapseStreamingSteps` in editor-plugin-collab-edit.
	const collapseStreamingStepsEnabled = fg('platform_editor_ai_collapse_streaming_steps');

	// Hold the collab flush back while a block is still being written, so the frames that make up
	// that block collapse into one step rather than being sent individually.
	//
	// The decision is semantic only: flush once another block is complete, so peers receive whole
	// blocks rather than half-written sentences. How long a hold may last is bounded by
	// `createCollabSendHold` in the collab plugin, not here. Keeping the time bound in one place
	// avoids two independently tuned windows that can drift out of order with each other.
	//
	// The first frame is held like any other. Flushing it would lock it via `lockStepOrigins`, and
	// a locked step can never be composed, which made the first two frames of every stream
	// permanently unmergeable.
	//
	// `countCompleteBlocks` counts *top-level* blocks, so content streaming into a single large
	// node — a table filling in row by row — reports no new complete block until the whole node
	// closes. Those frames are released by the hold's time bound rather than here.
	const completeBlocks = collapseStreamingStepsEnabled
		? countCompleteBlocks({ adfResponse, markdown })
		: 0;
	const flushCollabSteps =
		!collapseStreamingStepsEnabled || completeBlocks > collabFlushedBlockCount;

	if (collapseStreamingStepsEnabled) {
		tr = tr.setMeta(STREAMING_COLLAPSIBLE_STEPS, true);

		// Set explicitly either way: the hold spans transactions, so a flushing frame has to
		// actively release it. Leaving the metadata off would only mean "no opinion", and the hold
		// would stay on until the backstop expired.
		tr = tr.setMeta(DEFER_COLLAB_SEND, !flushCollabSteps);
	}

	tr = metaTransaction({
		tr,
		isFinalStream: false,
		stackTime: streamTimeStamp,
		agentAttribution: resolveAgentAttribution({
			sessionAgentAttribution: sessionState.agentAttribution,
			configItemKey: decorationPluginState?.configItem?.key,
		}),
	});

	// Map positions AFTER document changes
	const newDocSize = tr.doc.content.size;
	const mappedStartPos = clampPosition(mapStartPos({ pos: workingStartPos, tr }), newDocSize);
	const mappedUserContentEndPos = clampPosition(
		mapToPos({ pos: workingUserContentEndPos, tr }),
		newDocSize,
	);
	const mappedAiContentEndPos = clampPosition(mapToPos({ pos: toPos, tr }), newDocSize);

	// Calculate telepointer
	const telepointerPos = calculateTelepointerPos(tr.doc, mappedStartPos, mappedAiContentEndPos);

	// Use original transform with extended payload
	tr = updateStreamPositions({
		aiContentEndPos: mappedAiContentEndPos,
		telepointerPos,
		isFinalStream: false,
		lastProcessedVersion: responseVersion,
		aiContentSlice: slice,
		scrollTargetPos: mappedAiContentEndPos,
		posAdjusted: workingPosAdjusted,
		startPos: mappedStartPos,
		userContentEndPos: mappedUserContentEndPos,
		...(flushCollabSteps && { collabFlushedBlockCount: completeBlocks }),
	})(tr);

	// Quick Actions stream a whole block at a time but only commit once, so the
	// chrome would appear in one go at the end. Restamping per chunk walks it to
	// the end of the content written so far, matching the per-tool-call cadence
	// other experiences already get from their own commits.
	//
	// Scoped to Quick Actions deliberately: experiences that commit per tool call
	// are already stamped at the right cadence, and restamping mid-tool-call would
	// change their behaviour for no benefit.
	//
	// The shared detector decides which content actually changed in this chunk.
	if (isQuickActionConfigItem(decorationPluginState?.configItem?.key)) {
		tr = stampUnifiedAgentEditChrome(tr, state);
	}

	// Fire the loading→complete analytics step once (guarded by lastQueuedStep in AnalyticsBridge).
	// This triggers ACTION.INITIATED, the userFlow 'loading' step event, and sets up aiInteractionID
	// for downstream steps. ACTION.VIEWED is NOT tied to this — it fires on 'complete' (after stream ends).
	fireAnalyticsStep('loading', 'complete');

	return tr;
}

function buildPositionOnlyUpdate(
	state: EditorState,
	streamingState: PurePmInEditorDirectStreamingPluginState,
	responseVersion: number,
): Transaction {
	const { aiContentEndPos, telepointerPos, startPos, aiContentSlice } = streamingState;

	let tr = state.tr;
	tr = updateStreamPositions({
		aiContentEndPos: aiContentEndPos ?? startPos ?? 0,
		telepointerPos: telepointerPos ?? startPos ?? 0,
		isFinalStream: false,
		lastProcessedVersion: responseVersion,
		aiContentSlice,
	})(tr);

	return tr;
}
